import {
    Alert, Avatar, Button, Card, Col, Divider, Image, Input, Modal, Row, Select, Spin, Table, Tag, Timeline, Typography
} from 'antd';
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { apiCall } from '../../API/RestApi';
import { modalError } from '../../Component/Modal/ModalLayout';
import { useSelector } from 'react-redux';
import Search from 'antd/es/input/Search';
import { dateFormat } from '../../Utils/formatDate';
import { ArrowRightOutlined } from '@ant-design/icons';

const { Option } = Select;

function DelegationChangeShift() {
    const token = useSelector((state) => state.user.token);
    const [data, setData] = useState([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState({ pagination: { current: 1, pageSize: 10 } });
    const [isAlert, setIsAlert] = useState(false);
    const [textAlert, setTextAlert] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [detailData, setDetailData] = useState();
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);
    const [rejectReason, setRejectReason] = useState('');
    const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);

    const titlePage = 'Change Shift Delegation';

    const columns = [
        {
            title: 'No',
            render: (_, __, index) =>
                (page.pagination.current - 1) * page.pagination.pageSize + index + 1
        },
        {
            title: 'User Code',
            dataIndex: ['users', 'users_code']
        },
        {
            title: 'Name',
            dataIndex: ['users', 'name']
        },
        {
            title: 'Date',
            dataIndex: 'date',
            render: (text) => dateFormat(text)
        },
        {
            title: 'Reason',
            dataIndex: 'reason'
        },
        {
            title: 'Status',
            dataIndex: 'delegation_status',
            render: (status) => {
                const color = status === 'approved' ? 'green' : status === 'rejected' ? 'red' : 'yellow';
                return <Tag color={color}>{status}</Tag>;
            }
        },
        {
            title: 'Action',
            render: (_, record) => (
                <Button onClick={() => showDetailModal(record)}>Detail</Button>
            )
        }
    ];

    const showDetailModal = (record) => {
        setDetailData(record);
        setIsModalVisible(true);
    };

    const getApi = () => {
        setLoading(true);
        apiCall({
            method: 'GET',
            url: `/changeshift?byDelegation=true&search=${search}&page=${page.pagination.current}&statusDelegation=${statusFilter}`,
            data: { headers: { Authorization: `Bearer ${token}` } }
        })
            .then((res) => {
                if (res.status === 'success') {
                    setData(res.data.data);
                    setPage({ ...page, pagination: { ...page.pagination, total: res.data.total_data } });
                }
            })
            .finally(() => setLoading(false));
    };

    const postApi = (values) => {
        setLoading(true);
        return apiCall({
            method: 'PUT',
            url: '/changeshift/delegationbulk',
            data: {
                data: values,
                headers: { Authorization: `Bearer ${token}` }
            }
        }).finally(() => setLoading(false));
    };

    const handleBulkAction = (actionType) => {
        if (actionType === 'reject') {
            setIsRejectModalVisible(true);
        } else {
            Modal.confirm({
                title: 'CONFIRMATION',
                content: `Approve ${selectedRowKeys.length} request(s)?`,
                onOk: async () => {
                    const ids = selectedRows.map((item) => item.shift_change_request_id);
                    await postApi({ ids, status_approval: 'approved' });
                    setTextAlert(`Approved ${ids.length} request(s).`);
                    setIsAlert(true);
                    resetSelections();
                    getApi();
                }
            });
        }
    };

    const submitRejectReason = async () => {
        if (!rejectReason.trim()) {
            modalError("Reason is required for rejection.");
            return;
        }
        const ids = selectedRows.map((item) => item.shift_change_request_id);
        await postApi({ ids, status_approval: 'rejected', reason: rejectReason });
        setTextAlert(`Rejected ${ids.length} request(s).`);
        setIsAlert(true);
        setRejectReason('');
        setIsRejectModalVisible(false);
        resetSelections();
        getApi();
    };

    const resetSelections = () => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
    };

    const handleTableChange = (pagination) => {
        setPage({ ...page, pagination });
    };

    const searchData = (value) => {
        setSearch(value);
        setPage({ pagination: { current: 1, pageSize: 10 } });
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys, rows) => {
            setSelectedRowKeys(keys);
            setSelectedRows(rows);
        },
        getCheckboxProps: (record) => ({
            disabled: record.delegation_status !== 'pending',
        })
    };

    useEffect(() => {
        getApi();
    }, [JSON.stringify(page), search, statusFilter]);

    return (
        <div>
            <Helmet><title>{titlePage}</title></Helmet>
            {isAlert && <Alert message={textAlert} type="success" banner closable onClose={() => setIsAlert(false)} />}
            <h3>{titlePage}</h3>
            <Row gutter={[16, 16]}>
                <Col span={8}>
                    <Search placeholder="Search" enterButton allowClear onSearch={searchData} />
                </Col>
                <Col span={8}>
                    <Select
                        placeholder="Filter by status"
                        allowClear
                        style={{ width: 160 }}
                        onChange={setStatusFilter}
                    >
                        <Option value="pending">Pending</Option>
                        <Option value="approved">Approved</Option>
                        <Option value="rejected">Rejected</Option>
                    </Select>
                </Col>
            </Row>
            <Row style={{ margin: '16px 0' }}>
                <Col>
                    <Button
                        type="primary"
                        onClick={() => handleBulkAction('approve')}
                        disabled={!selectedRowKeys.length}
                        style={{ marginRight: 8 }}
                    >
                        Approve ({selectedRowKeys.length})
                    </Button>
                    <Button
                        danger
                        onClick={() => handleBulkAction('reject')}
                        disabled={!selectedRowKeys.length}
                    >
                        Reject ({selectedRowKeys.length})
                    </Button>
                </Col>
            </Row>
            <Table
                rowKey="shift_change_request_id"
                rowSelection={rowSelection}
                columns={columns}
                dataSource={data}
                pagination={page.pagination}
                loading={loading}
                onChange={handleTableChange}
            />
            <Modal
                title="Reject Reason"
                open={isRejectModalVisible}
                onOk={submitRejectReason}
                onCancel={() => setIsRejectModalVisible(false)}
            >
                <Input.TextArea
                    rows={4}
                    placeholder="Enter reason for rejection"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                />
            </Modal>

            <Modal
                title="Change Shift Detail"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                footer={null}
                width={800}
            >
                {detailData ? (
                    <Card>
                        <Row gutter={[16, 16]}>
                            <Col span={6}>
                                <Image src={detailData.users?.image} width={120} />
                            </Col>
                            <Col span={18}>
                                <Typography.Title level={5}>{detailData.users?.name}</Typography.Title>
                                <p>User Code: {detailData.users?.users_code}</p>
                                <p>Date: {dateFormat(detailData.date)}</p>
                                <p>Reason: {detailData.reason}</p>
                                <Tag color={detailData.approval_status === 'approved' ? 'green' : detailData.approval_status === 'rejected' ? 'red' : 'yellow'}>
                                    {detailData.approval_status}
                                </Tag>
                            </Col>
                        </Row>
                        <Divider />
                        <Typography.Title level={5}>Change Shift</Typography.Title>
                        <Row align="middle" gutter={[8, 8]} justify="center">
                            <Col span={10}>
                                <Card size="small" title="Old Shift">
                                    {detailData.old_shift ? (
                                        <>
                                            <p><strong>{detailData.old_shift.name}</strong></p>
                                            <p>Time In: {detailData.old_shift.time_in}</p>
                                            <p>Time Out: {detailData.old_shift.time_out}</p>
                                        </>
                                    ) : <p>-</p>}
                                </Card>
                            </Col>

                            <Col span={2} style={{ textAlign: 'center' }}>
                                <ArrowRightOutlined style={{ fontSize: 24 }} />
                            </Col>

                            <Col span={10}>
                                <Card size="small" title="New Shift">
                                    {detailData.new_shift ? (
                                        <>
                                            <p><strong>{detailData.new_shift.name}</strong></p>
                                            <p>Time In: {detailData.new_shift.time_in}</p>
                                            <p>Time Out: {detailData.new_shift.time_out}</p>
                                        </>
                                    ) : <p>-</p>}
                                </Card>
                            </Col>
                        </Row>

                        <Divider />
                        <Typography.Title level={5}>Delegation Timeline</Typography.Title>
                        <br />
                        <Timeline mode="left">
                            <Timeline.Item color="blue">
                                <Row gutter={12} align="top">
                                    <Col>
                                        <Avatar src={detailData.users?.image} />
                                    </Col>
                                    <Col>
                                        <Typography.Text strong>{detailData.users?.name}</Typography.Text><br />
                                        <Tag color="blue">Requested</Tag>
                                        <p style={{ margin: 2 }}>Reason: {detailData.reason}</p>
                                        <p style={{ margin: 2, fontSize: 11, color: 'grey' }}>{dateFormat(detailData.requested_at)}</p>
                                    </Col>
                                </Row>
                            </Timeline.Item>

                            {detailData.delegation_status !== 'pending' && detailData.delegation_at && (
                                <Timeline.Item
                                    color={detailData.delegation_status === 'approved' ? 'green' : 'red'}
                                >
                                    <Row gutter={12} align="top">
                                        <Col>
                                            <Avatar src={detailData.delegation?.image} />
                                        </Col>
                                        <Col>
                                            <Typography.Text strong>{detailData.delegation?.name}</Typography.Text><br />
                                            <Tag color={detailData.delegation_status === 'approved' ? 'green' : 'red'}>
                                                {detailData.delegation_status}
                                            </Tag>
                                            <p style={{ margin: 2, fontSize: 11, color: 'grey' }}>{dateFormat(detailData.delegation_at)}</p>
                                        </Col>
                                    </Row>
                                </Timeline.Item>
                            )}

                            {detailData.approval_status !== 'pending' && detailData.approval_at && (
                                <Timeline.Item
                                    color={detailData.approval_status === 'approved' ? 'green' : 'red'}
                                >
                                    <Row gutter={12} align="top">
                                        <Col>
                                            <Avatar src={detailData.approved?.image} />
                                        </Col>
                                        <Col>
                                            <Typography.Text strong>{detailData.approved?.name}</Typography.Text><br />
                                            <Tag color={detailData.approval_status === 'approved' ? 'green' : 'red'}>
                                                {detailData.approval_status}
                                            </Tag>
                                            {detailData.reason_approval != '' && (
                                                <p style={{ margin: 2 }}>Reason: {detailData.reason_approval}</p>
                                            )}
                                            <p style={{ margin: 2, fontSize: 11, color: 'grey' }}>{dateFormat(detailData.approval_at)}</p>
                                        </Col>
                                    </Row>
                                </Timeline.Item>
                            )}
                        </Timeline>


                    </Card>
                ) : <Spin />}
            </Modal>
        </div>
    );
}

export default DelegationChangeShift;
